import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { pretsAPI } from '../../../services/api';
import { Pret, PretCreate, PretUpdate } from '../../../types/pret';
import { EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import PriceInput, { parsePriceValue } from '../../../components/PriceInput';
import {
  SlideUpTransition,
  useMinimizable,
  DialogControls,
  MinimizedBar,
} from '../../../components/MinimizableDialog';

const HR_COLOR = '#00897B';

interface PretFormDialogProps {
  open: boolean;
  onClose: () => void;
  pret: Pret | null;
  employees: EmployeeDropdownItem[];
  onSuccess: () => void;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const PretFormDialog: React.FC<PretFormDialogProps> = ({
  open,
  onClose,
  pret,
  employees,
  onSuccess,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [formData, setFormData] = useState<{
    employee_id: string;
    montant_pret: number | null | string;
    prelevement_mensuel: number | null | string;
    date_demande: string;
    date_debut_prelevement: string;
    motif: string;
  }>({
    employee_id: '',
    montant_pret: null,
    prelevement_mensuel: null,
    date_demande: new Date().toISOString().split('T')[0],
    date_debut_prelevement: '',
    motif: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { showSuccess, showError } = useAlert();

  // Draft caching — only for new prêts (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: typeof formData;
  }>('pret', !pret);

  useEffect(() => {
    if (open) {
      if (pret) {
        setFormData({
          employee_id: pret.employee_id.toString(),
          montant_pret: pret.montant_pret,
          prelevement_mensuel: pret.prelevement_mensuel,
          date_demande: pret.date_demande,
          date_debut_prelevement: pret.date_debut_prelevement || '',
          motif: pret.motif || '',
        });
      } else {
        // New mode — repopulate from a draft if one was stashed by an accidental
        // backdrop/Escape close, otherwise do the blank reset.
        const draft = loadDraft();
        if (draft) {
          setFormData(draft.formData);
        } else {
          setFormData({
            employee_id: '',
            montant_pret: null,
            prelevement_mensuel: null,
            date_demande: new Date().toISOString().split('T')[0],
            date_debut_prelevement: '',
            motif: '',
          });
        }
      }
      setError(null);
    }
  }, [open, pret, loadDraft]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handlePriceChange = (name: string, value: number | null | string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const calculateDuration = (): number => {
    const montant = parsePriceValue(formData.montant_pret);
    const mensualite = parsePriceValue(formData.prelevement_mensuel);
    if (mensualite <= 0) return 0;
    return Math.ceil(montant / mensualite);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.employee_id) {
      setError('Veuillez sélectionner un employé');
      return;
    }
    const montantPret = parsePriceValue(formData.montant_pret);
    const prelevementMensuel = parsePriceValue(formData.prelevement_mensuel);

    if (montantPret <= 0) {
      setError('Le montant du prêt doit être supérieur à 0');
      return;
    }
    if (prelevementMensuel <= 0) {
      setError('Le prélèvement mensuel doit être supérieur à 0');
      return;
    }
    if (!formData.date_demande) {
      setError('La date de demande est requise');
      return;
    }

    setLoading(true);
    try {
      if (pret) {
        const updateData: PretUpdate = {
          montant_pret: montantPret,
          prelevement_mensuel: prelevementMensuel,
          date_demande: formData.date_demande,
          date_debut_prelevement: formData.date_debut_prelevement || null,
          motif: formData.motif || null,
        };
        await pretsAPI.update(pret.id, updateData);
        showSuccess('Demande de prêt modifiée avec succès');
      } else {
        const createData: PretCreate = {
          employee_id: parseInt(formData.employee_id),
          montant_pret: montantPret,
          prelevement_mensuel: prelevementMensuel,
          date_demande: formData.date_demande,
          date_debut_prelevement: formData.date_debut_prelevement || null,
          motif: formData.motif || null,
        };
        await pretsAPI.create(createData);
        showSuccess('Demande de prêt créée avec succès');
      }
      clearDraft();
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const duration = calculateDuration();

  // Annuler = deliberate cancel: discard any draft, then close.
  const handleClose = () => {
    clearDraft();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (!pret) {
        saveDraft({ formData });
      }
      onClose();
    }
  };

  return (
    <>
    <Dialog
      open={dialogOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={SlideUpTransition}
    >
      <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {pret ? 'Modifier la Demande de Prêt' : 'Nouvelle Demande de Prêt'}
          <DialogControls color="white" onMinimize={minimize} onClose={handleClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small" disabled={!!pret}>
              <InputLabel>Employé *</InputLabel>
              <Select
                value={formData.employee_id}
                onChange={(e) => handleSelectChange('employee_id', e.target.value)}
                label="Employé *"
              >
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.id.toString()}>
                    {emp.matricule} - {emp.nom_complet}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <PriceInput
              value={formData.montant_pret}
              onChange={(value) => handlePriceChange('montant_pret', value)}
              label="Montant du Prêt *"
              currency="DH"
              required
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <PriceInput
              value={formData.prelevement_mensuel}
              onChange={(value) => handlePriceChange('prelevement_mensuel', value)}
              label="Prélèvement Mensuel *"
              currency="DH"
              required
              variant="outlined"
            />
          </Grid>

          {duration > 0 && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Durée estimée du prêt:
                </Typography>
                <Typography variant="h6" sx={{ color: HR_COLOR }}>
                  {duration} mois
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Date de Demande *"
              name="date_demande"
              type="date"
              value={formData.date_demande}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Date Début Prélèvement"
              name="date_debut_prelevement"
              type="date"
              value={formData.date_debut_prelevement}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              helperText="Optionnel - peut être défini lors de la validation"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Motif / Raison"
              name="motif"
              multiline
              rows={3}
              value={formData.motif}
              onChange={handleChange}
              placeholder="Raison de la demande de prêt..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
        >
          {loading ? <CircularProgress size={24} /> : (pret ? 'Modifier' : 'Créer')}
        </Button>
      </DialogActions>
    </Dialog>

    <MinimizedBar
      open={open && minimized}
      color="#009688"
      title={pret ? 'Modifier la Demande de Prêt' : 'Nouvelle Demande de Prêt'}
      onRestore={restore}
      onClose={handleClose}
    />
    </>
  );
};

export default PretFormDialog;
